import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { CalendarToday, Person, AccessTime } from '@mui/icons-material';
import contentService, { BlogPost } from '../services/contentService';
import { translateWithCache } from '../services/translation';

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { i18n } = useTranslation();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) return;

      try {
        const postData = await contentService.getBlogPost(slug);
        const lang = (i18n.language || 'es').split('-')[0];

        let translatedPost = postData;

        if (lang === 'en') {
          // Translate blog post
          translatedPost = {
            ...postData,
            title: await translateWithCache(postData.title, 'es', 'en'),
            content: await translateWithCache(postData.content, 'es', 'en'),
            excerpt: postData.excerpt ? await translateWithCache(postData.excerpt, 'es', 'en') : postData.excerpt,
            tags: await Promise.all(postData.tags.map(async (tag) => await translateWithCache(tag, 'es', 'en'))),
          };
        }

        setPost(translatedPost);
      } catch (err: any) {
        console.error('Error fetching blog post:', err);
        setError('Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug, i18n.language]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !post) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Blog post not found'}</Alert>
      </Container>
    );
  }

  return (
    <Box>
      {/* Hero Section */}
      {post.featured_image && (
        <Box
          sx={{
            position: 'relative',
            height: '50vh',
            minHeight: '400px',
            backgroundImage: `url(${post.featured_image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.4)',
            }}
          />
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ textAlign: 'center', color: 'white' }}>
              <Typography
                variant="h1"
                gutterBottom
                sx={{
                  fontFamily: '"Bebas Neue", sans-serif',
                  fontWeight: 400,
                  mb: 2,
                }}
              >
                {post.title}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, justifyContent: 'center', opacity: 0.9 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarToday fontSize="small" />
                  <Typography variant="body2">
                    {post.published_at ? formatDate(post.published_at) : formatDate(post.created_at)}
                  </Typography>
                </Box>
                {post.author && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Person fontSize="small" />
                    <Typography variant="body2">
                      {post.author}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Container>
        </Box>
      )}

      {/* Post Content */}
      <Box sx={{ py: 8 }}>
        <Container maxWidth="md">
          {/* Post Meta */}
          {!post.featured_image && (
            <Box sx={{ mb: 6 }}>
              <Typography
                variant="h1"
                gutterBottom
                sx={{
                  fontFamily: '"Bebas Neue", sans-serif',
                  fontWeight: 400,
                  mb: 3,
                }}
              >
                {post.title}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3, color: 'text.secondary' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarToday fontSize="small" />
                  <Typography variant="body2">
                    {post.published_at ? formatDate(post.published_at) : formatDate(post.created_at)}
                  </Typography>
                </Box>
                {post.author && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Person fontSize="small" />
                    <Typography variant="body2">
                      {post.author}
                    </Typography>
                  </Box>
                )}
                {post.reading_time && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AccessTime fontSize="small" />
                    <Typography variant="body2">
                      {post.reading_time} min de lectura
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 4 }}>
              {post.tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          )}

          {/* Post Content */}
          <Box sx={{ mb: 6 }}>
            <div
              dangerouslySetInnerHTML={{ __html: post.content }}
              style={{
                lineHeight: 1.8,
                fontSize: '1.1rem',
                color: '#513628',
              }}
            />
          </Box>

          {/* Post Footer */}
          <Divider sx={{ mb: 4 }} />
          <Box sx={{ textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">
              Publicado el {post.published_at ? formatDate(post.published_at) : formatDate(post.created_at)}
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default BlogPostPage; 